import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  deleteWikiFileMutation,
  getWikiFileOptions,
  listWikiFilesOptions,
  listWikiFilesQueryKey,
  saveWikiFileMutation,
  type WikiFileResponse,
} from "@/api";

export type WikiVisibility = WikiFileResponse["visibility"];

export function useWikiFiles(spaceUuid: string) {
  return useQuery({ ...listWikiFilesOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data });
}

export function useWikiFile(spaceUuid: string, path: string | null) {
  return useQuery({
    ...getWikiFileOptions({ path: { space_uuid: spaceUuid }, query: { path: path ?? "" } }),
    enabled: path !== null,
    select: (r) => r.data,
  });
}

/** Translate a wiki API error by its code; anything else falls back. */
export function useWikiErrorMessage() {
  const { t } = useTranslation("knowledge");
  return (error: unknown, fallback: string): string => {
    if (!(error instanceof ApiError)) return fallback;
    switch (error.code) {
      case "WIKI_CONFLICT":
        return t("errors.WIKI_CONFLICT");
      case "WIKI_PUBLISH_FORBIDDEN":
        return t("errors.WIKI_PUBLISH_FORBIDDEN");
      case "WIKI_FILE_NOT_FOUND":
        return t("errors.WIKI_FILE_NOT_FOUND");
      case "WIKI_INVALID_PATH":
        return t("errors.WIKI_INVALID_PATH", { detail: error.detail ?? "" });
      default:
        return error.detail ?? fallback;
    }
  };
}

function useInvalidateWiki(spaceUuid: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listWikiFilesQueryKey({ path: { space_uuid: spaceUuid } }) }),
      // A save can change other pages' links and backlinks, so refresh every open page.
      queryClient.invalidateQueries({
        predicate: (query) => (query.queryKey[0] as { _id?: string } | undefined)?._id === "getWikiFile",
      }),
    ]);
}

/** Create (`baseRevision: null`) or update a page. Errors are left to the caller (conflicts need special UI). */
export function useSaveWikiFile(spaceUuid: string) {
  const invalidate = useInvalidateWiki(spaceUuid);
  return useMutation({ ...saveWikiFileMutation(), onSuccess: () => invalidate() });
}

export function useDeleteWikiFile(spaceUuid: string, onDeleted: () => void) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useWikiErrorMessage();
  const invalidate = useInvalidateWiki(spaceUuid);
  return useMutation({
    ...deleteWikiFileMutation(),
    onSuccess: async () => {
      onDeleted();
      await invalidate();
      toast.success(t("remove.deleted"));
    },
    onError: (error) => toast.error(message(error, t("remove.failed"))),
  });
}
